import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ROWS_PER_PAGE } from '../utils/paginationUtils';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import NoData from './NoData';
import { Link } from '@mui/material';

const getTicketStatusChip = (statusValue, t) => {
  const status = statusValue || 'UNKNOWN';
  const color =
    status === 'VALID'
      ? 'success'
      : status === 'SCANNED'
      ? 'info'
      : status === 'REFUND_PENDING'
      ? 'warning'
      : status === 'CANCELED'
      ? 'error'
      : 'default';

  return <Chip size="small" label={t(status)} color={color} variant="outlined" sx={{ borderRadius: 1 }} />;
};

export default function TicketsTable({ userRole, tickets, setSelectedOrdId, seatIdToSectionParentOwn, pagination }) {
  const { t } = useTranslation();
  const { totalItems, page, limit, onPageChange, onRowsPerPageChange } = pagination;

  return (
    <>
      {tickets.length ? (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer sx={{ mt: 2 }} component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& > th': { whiteSpace: 'nowrap' } }}>
                  <TableCell align="left">{t('Nr.')}</TableCell>
                  <TableCell align="left">{t('Date/Time')}</TableCell>
                  <TableCell align="left">{t('Category')}</TableCell>
                  <TableCell align="left">{t('Price')}</TableCell>
                  <TableCell align="left">{t('Order ID')}</TableCell>
                  <TableCell align="left">{t('Seat ID')}</TableCell>
                  <TableCell align="left">{t('Serial')}</TableCell>
                  <TableCell align="left">{t('Fiscal Serial')}</TableCell>
                  <TableCell align="left">{t('Email')}</TableCell>
                  <TableCell align="left">{t('Name')}</TableCell>
                  <TableCell align="left">{t('Phone')}</TableCell>
                  <TableCell align="left">{t('StatusLabel')}</TableCell>
                  {userRole !== 'cashier' && <TableCell align="left">{t('Originator')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map(
                  (
                    {
                      id,
                      status,
                      canceled,
                      type_label,
                      is_default_type,
                      created_at,
                      serial,
                      fiscal_serial,
                      seatsio_object_id,
                      section,
                      parent,
                      own,
                      email,
                      first_name,
                      last_name,
                      phone,
                      price,
                      category_name,
                      is_organizer,
                      is_cashier,
                    },
                    index
                  ) => {
                    const ticketStatus = String((canceled ? 'CANCELED' : status) ?? '').toUpperCase();
                    const isTicketUnavailable = ['REFUNDED', 'CANCELED'].includes(ticketStatus) || Boolean(canceled);

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          textAlign: 'right',
                          '& > td': { whiteSpace: 'nowrap' },
                          '&:last-child td, &:last-child th': { borderBottom: 0 },
                          '&:nth-of-type(odd)': (theme) => ({
                            backgroundColor: theme.palette.action.hover,
                          }),
                        }}
                      >
                        <TableCell>{limit * (page - 1) + index + 1}</TableCell>
                        <TableCell>{dayjs(created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                        <TableCell>
                          {category_name}
                          {!is_default_type && <Chip sx={{ marginLeft: 1 }} size="small" label={type_label} />}
                        </TableCell>
                        <TableCell>{price + ' Lei'}</TableCell>
                        <TableCell>
                          <Link
                            onClick={() => setSelectedOrdId(id)}
                            sx={{
                              textDecoration: isTicketUnavailable ? 'line-through' : 'unset',
                              color: isTicketUnavailable ? 'red' : 'white',
                              cursor: 'pointer',
                            }}
                            underline="hover"
                          >
                            {id}
                          </Link>
                        </TableCell>
                        <TableCell>{seatIdToSectionParentOwn(seatsio_object_id, section, parent, own, t)}</TableCell>
                        <TableCell>{serial}</TableCell>
                        <TableCell>{fiscal_serial || '-'}</TableCell>
                        <TableCell>{email || '-'}</TableCell>
                        <TableCell>
                          {first_name || last_name ? `${first_name || ''} ${last_name || ''}`.trim() : '-'}
                        </TableCell>
                        <TableCell>{phone || '-'}</TableCell>
                        <TableCell>{getTicketStatusChip(ticketStatus, t)}</TableCell>
                        {userRole !== 'cashier' && (
                          <TableCell>{is_cashier ? 'cashier' : is_organizer ? 'organizer' : 'client'}</TableCell>
                        )}
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page - 1}
            onPageChange={onPageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE}
            labelRowsPerPage={t('rowsPaginator')}
          />
        </Paper>
      ) : (
        <NoData />
      )}
    </>
  );
}
